import { SiteCode, type SiteCreate, type SiteDetail } from '@tabel/shared/schemas/sites';
import { CITIES, OTHER_CITY, RU_TZ_OFFSETS, resolveCityTz } from '@tabel/shared/sites/cities';
import { STEP_BODY, tzHint } from '@tabel/shared/sites/step-texts';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { errorText, useToast } from '../../../components/ErrorToast';
import { useUnsavedChanges, useUnsavedGuard } from '../../../components/UnsavedGuard';
import { Button, Modal, inputCls } from '../../../components/ui';
import { ApiError } from '../../../lib/errors';
import { createSite } from '../api';
import { FormField, SCREEN } from '../parts';
import { CREATE } from '../texts';

/*
 * Окно «Подключить площадку» — шаг 1 (007 T039; spec.md FR-017, US1 сценарий 2; research R15; прототип, шаг 1):
 * название и город. Пояс ставится по городу и показывается примером «08:00 на площадке = 08:00 по Екатеринбургу».
 * Своего города нет в списке — «Другой город»: название и пояс России. Код площадки сервер выбирает сам; если такой
 * код уже занят (`SITE_CODE_TAKEN`), окно просит ввести код. Ошибки сервера по полям — у поля, остальные — тостом.
 */

type Errors = Partial<Record<'name' | 'city' | 'otherCity' | 'tz' | 'code', string>>;

export function CreateSiteDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (site: SiteDetail) => void;
}) {
  const id = useId();
  const { showError } = useToast();
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [otherCity, setOtherCity] = useState('');
  const [tz, setTz] = useState('');
  const [code, setCode] = useState('');
  /** Поле «Код площадки» — только после `SITE_CODE_TAKEN`. */
  const [askCode, setAskCode] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) return;
    setName('');
    setCity('');
    setOtherCity('');
    setTz('');
    setCode('');
    setAskCode(false);
    setErrors({});
    setBusy(false);
  }, [open]);

  /** Ввод в поле снимает его ошибку: она относилась к прежнему значению. */
  const edit = (field: keyof Errors, set: (v: string) => void) => (v: string) => {
    set(v);
    setErrors(({ [field]: _, ...rest }) => rest);
  };

  const other = city === OTHER_CITY;
  const cityName = other ? otherCity.trim() : city;
  const zone = city ? resolveCityTz(cityName || city, other ? tz : undefined) : null;

  const check = (): Errors => {
    const e: Errors = {};
    if (!name.trim()) e.name = CREATE.errors.name;
    if (!city) e.city = CREATE.errors.city;
    if (other && !otherCity.trim()) e.otherCity = CREATE.errors.otherCity;
    if (other && !RU_TZ_OFFSETS.includes(tz)) e.tz = CREATE.errors.tz;
    if (askCode && !SiteCode.safeParse(code.trim()).success) e.code = CREATE.errors.code;
    return e;
  };

  /** Создать площадку; не вышло — `false`: ошибка у поля или тостом, окно остаётся с введённым. */
  const create = async (): Promise<boolean> => {
    if (busy) return false;
    const e = check();
    setErrors(e);
    if (Object.keys(e).length > 0) return false;
    const body: SiteCreate = {
      name: name.trim(),
      city: cityName,
      ...(other ? { tzOffset: tz } : {}),
      ...(askCode ? { code: code.trim() } : {}),
    };
    setBusy(true);
    try {
      onCreated(await createSite(body));
      return true;
    } catch (err) {
      setBusy(false);
      const reason = err instanceof ApiError ? err.code : null;
      if (reason === 'SITE_NAME_TAKEN') setErrors({ name: errorText(err) });
      else if (reason === 'SITE_CODE_TAKEN') {
        setAskCode(true);
        setErrors({ code: errorText(err) });
      } else if (reason === 'CITY_UNKNOWN') setErrors({ city: errorText(err) });
      else showError(err, { screen: SCREEN, action: 'create' });
      return false;
    }
  };
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    void create();
  };

  // Защита несохранённого (T047; FR-008, ux.md §4): закрыть окно с введённым — сначала вопрос «Подключить площадку?»;
  // ввод не дописан — «Дописать» и «Не подключать». Закрытие ✕, Esc и «Отмена» идут через `guard`.
  const { guard } = useUnsavedGuard();
  const typed = open && ([name, otherCity, code].some((v) => v.trim() !== '') || city !== '' || tz !== '');
  useUnsavedChanges(
    typed && !busy
      ? {
          kind: 'site',
          summary: CREATE.unsaved(name.trim()),
          canSave: Object.keys(check()).length === 0,
          save: async () => {
            if (!(await create())) throw new Error('Площадка не подключена');
          },
        }
      : null,
  );
  const close = () => guard(onClose);

  return (
    <Modal open={open} onClose={close} title={CREATE.title} description={CREATE.description}>
      <form noValidate onSubmit={(e) => void submit(e)}>
        <div className="mb-5 rounded-control bg-subtle px-3.5 py-3">
          <p className="text-[13px] font-semibold text-ink">{CREATE.needTitle}</p>
          <ul className="mt-1.5 list-disc space-y-1 pl-5 text-[13px] text-ink-2">
            {CREATE.need.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
          <FormField id={`${id}-name`} label={STEP_BODY[1].nameLabel} error={errors.name}>
            {(a) => (
              <input
                {...a}
                className={inputCls}
                value={name}
                maxLength={60}
                autoComplete="off"
                placeholder={CREATE.namePlaceholder}
                data-autofocus
                onChange={(e) => edit('name', setName)(e.target.value)}
              />
            )}
          </FormField>
          <FormField
            id={`${id}-city`}
            label={STEP_BODY[1].cityLabel}
            error={errors.city}
            hint={`${CREATE.cityHint}${zone && !other ? ` ${tzHint(zone)}` : ''}`}
          >
            {(a) => (
              <select
                {...a}
                className={inputCls}
                value={city}
                onChange={(e) => edit('city', setCity)(e.target.value)}
              >
                <option value="">{CREATE.cityPlaceholder}</option>
                {CITIES.map((c) => (
                  <option key={c.name} value={c.name}>
                    {`UTC${c.tzOffset} — ${c.name}`}
                  </option>
                ))}
                <option value={OTHER_CITY}>{OTHER_CITY}</option>
              </select>
            )}
          </FormField>
          {other && (
            <>
              <FormField id={`${id}-other`} label={STEP_BODY[1].otherCityLabel} error={errors.otherCity}>
                {(a) => (
                  <input
                    {...a}
                    className={inputCls}
                    value={otherCity}
                    maxLength={60}
                    placeholder={CREATE.otherCityPlaceholder}
                    onChange={(e) => edit('otherCity', setOtherCity)(e.target.value)}
                  />
                )}
              </FormField>
              <FormField
                id={`${id}-tz`}
                label={STEP_BODY[1].tzLabel}
                error={errors.tz}
                hint={zone ? tzHint(zone) : undefined}
              >
                {(a) => (
                  <select
                    {...a}
                    className={inputCls}
                    value={tz}
                    onChange={(e) => edit('tz', setTz)(e.target.value)}
                  >
                    <option value="">{CREATE.tzPlaceholder}</option>
                    {RU_TZ_OFFSETS.map((z) => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </select>
                )}
              </FormField>
            </>
          )}
          {askCode && (
            <FormField
              id={`${id}-code`}
              label={CREATE.codeLabel}
              hint={CREATE.codeHint}
              error={errors.code}
              className="col-span-2 max-sm:col-span-1"
            >
              {(a) => (
                <input
                  {...a}
                  className={`${inputCls} font-mono`}
                  value={code}
                  maxLength={20}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => edit('code', setCode)(e.target.value)}
                />
              )}
            </FormField>
          )}
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-2 max-sm:[&>button]:flex-auto">
          <Button onClick={close}>{CREATE.cancel}</Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {CREATE.submit}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
